import {
  ArrowDownOnSquareIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Save } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { DocumentListItem } from '@/components/documents/DocumentListItem';
import { DocumentSource } from '@/components/documents/documentSources';
import { DocumentUploadZone } from '@/components/documents/DocumentUploadZone';
import {
  sortFiles,
  SortKey,
  SortState,
} from '@/components/documents/sortFiles';
import { FileTtlSelect } from '@/components/FileTtlSelect';
import FileViewer from '@/components/FileViewer';
import { NoData } from '@/components/legacy-ui/NoData';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useDeleteFile, useDownloadFile, useMy, useUploadFile } from '@/hooks';
import { FileTtl } from '@/hooks/useFiles';
import { MyFile, ParsedFileTypes } from '@/types';
import { cn } from '@/utils/classname';
import { encode } from '@/utils/encrypt.util';
import { getFileType } from '@/utils/file.util';
import { useStorage } from '@/utils/storage/useStorage.util';

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onChange: (next: SortState) => void;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  sort,
  onChange,
  className,
}) => {
  const isActive = sort.key === sortKey;
  const next: SortState = isActive
    ? { key: sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
    : {
        key: sortKey,
        direction: sortKey === 'date' ? 'desc' : 'asc',
      };
  // Native button semantics are kept (no role override): columnheader/aria-sort
  // are only valid inside table/grid rows, so sort state goes in the name.
  return (
    <button
      type="button"
      onClick={() => onChange(next)}
      className={cn(
        'group flex items-center gap-1 text-left text-xs font-semibold uppercase',
        'text-chariot-text-secondary hover:text-chariot-text-primary',
        className
      )}
      aria-label={`Sort by ${label}${
        isActive
          ? `, ${sort.direction === 'asc' ? 'ascending' : 'descending'}`
          : ''
      }`}
    >
      <span className="truncate">{label}</span>
      {isActive ? (
        sort.direction === 'asc' ? (
          <ChevronUpIcon className="text-chariot-text-primary size-3.5 shrink-0" />
        ) : (
          <ChevronDownIcon className="text-chariot-text-primary size-3.5 shrink-0" />
        )
      ) : (
        <ChevronUpIcon className="size-3.5 shrink-0 opacity-0 group-hover:opacity-60" />
      )}
    </button>
  );
};

const FilesHeaderRow: React.FC<{
  sort: SortState;
  onChange: (next: SortState) => void;
}> = ({ sort, onChange }) => (
  <div className="border-chariot-stroke flex items-center gap-4 border-b px-4 py-2">
    <SortableHeader
      label="Name"
      sortKey="name"
      sort={sort}
      onChange={onChange}
      className="min-w-0 flex-1"
    />
    <SortableHeader
      label="Date"
      sortKey="date"
      sort={sort}
      onChange={onChange}
      className="w-32 shrink-0"
    />
    <SortableHeader
      label="Source"
      sortKey="source"
      sort={sort}
      onChange={onChange}
      className="w-28 shrink-0"
    />
    <div className="w-8 shrink-0"></div>
    <div className="w-8 shrink-0"></div>
  </div>
);

interface DocumentsSectionsProps {
  query?: string;
  customerEmail?: string;
  enableUpload?: boolean;
  enableSearch?: boolean;
  enableFavorites?: boolean;
  isPraetorianUser?: boolean; // Determines which section can upload
}

export const DocumentsSections: React.FC<DocumentsSectionsProps> = ({
  query = 'home',
  customerEmail,
  enableUpload = true,
  enableSearch = true,
  enableFavorites = true,
  isPraetorianUser = false, // Default to non-Praetorian for security — callers must explicitly opt in
}) => {
  // Role-based upload permissions
  const canUpload = enableUpload;
  const uploadSection = isPraetorianUser ? 'shared-with' : 'shared-by';
  const { mutateAsync: uploadFile } = useUploadFile(customerEmail);
  const { mutate: downloadFile } = useDownloadFile(customerEmail);
  const { mutateAsync: deleteFile } = useDeleteFile(customerEmail);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [uploadTtl, setUploadTtl] = useState<FileTtl | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filename, setFilename] = useState(
    () => searchParams.get('file') || ''
  );
  const [content, setContent] = useState<string | null>(null);
  const [originalContent, setOriginalContent] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({
    key: 'date',
    direction: 'desc',
  });
  const [favorites, setFavorites] = useStorage<string[]>(
    { key: 'files/favorites' },
    []
  );

  React.useEffect(() => {
    if (searchParams.get('file')) {
      setSearchParams(
        prev => {
          prev.delete('file');
          return prev;
        },
        { replace: true }
      );
    }
  }, []);

  const {
    data: sharedByCustomerFiles = [],
    invalidate: invalidateSharedByCustomerFiles,
    status: sharedByCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-by`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const {
    data: sharedWithCustomerFiles = [],
    invalidate: invalidateSharedWithCustomerFiles,
    status: sharedWithCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-with`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query PlexTrac reports stored in reports/ directory
  const {
    data: plextracReports = [],
    invalidate: invalidatePlextracReports,
    status: plextracReportsStatus,
  } = useMy(
    {
      resource: 'file',
      query: 'reports/',
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query export files stored under home/export-*
  const {
    data: exportFiles = [],
    invalidate: invalidateExportFiles,
    status: exportFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/export-`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query internal documents (Praetorian-only)
  const {
    data: internalFiles = [],
    invalidate: invalidateInternalFiles,
    status: internalFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/internal`,
      praetorian: true,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
      enabled: isPraetorianUser,
    }
  );

  const files = useMemo(() => {
    return [
      ...sharedByCustomerFiles,
      ...sharedWithCustomerFiles,
      ...plextracReports,
      ...exportFiles,
      ...(isPraetorianUser ? internalFiles : []),
    ];
  }, [
    sharedByCustomerFiles,
    sharedWithCustomerFiles,
    plextracReports,
    exportFiles,
    internalFiles,
    isPraetorianUser,
  ]);

  type TaggedFile = MyFile & {
    source: DocumentSource;
    displayName: string;
  };

  const taggedFiles = useMemo<TaggedFile[]>(() => {
    return [
      ...(isPraetorianUser
        ? internalFiles.map(f => ({
            ...f,
            source: 'internal' as const,
            displayName: f.name.replace(`${query}/internal/`, ''),
          }))
        : []),
      ...exportFiles.map(f => ({
        ...f,
        source: 'export' as const,
        displayName: f.name.replace(`${query}/`, ''),
      })),
      ...sharedWithCustomerFiles.map(f => ({
        ...f,
        source: 'shared-with' as const,
        displayName: f.name.replace(`${query}/shared-with/`, ''),
      })),
      ...plextracReports.map(f => ({
        ...f,
        source: 'report' as const,
        displayName: f.name.replace('reports/', ''),
      })),
      ...sharedByCustomerFiles.map(f => ({
        ...f,
        source: 'shared-by' as const,
        displayName: f.name.replace(`${query}/shared-by/`, ''),
      })),
    ];
  }, [
    sharedWithCustomerFiles,
    sharedByCustomerFiles,
    plextracReports,
    exportFiles,
    internalFiles,
    isPraetorianUser,
    query,
  ]);

  // Sort once; both lists below are order-preserving filters of this array.
  const sortedTaggedFiles = useMemo(
    () => sortFiles(taggedFiles, sort),
    [taggedFiles, sort]
  );

  const filteredFiles = useMemo(
    () =>
      sortedTaggedFiles.filter(file =>
        file.name.toLowerCase().includes(search.toLowerCase())
      ),
    [sortedTaggedFiles, search]
  );

  const sortedInternalFiles = useMemo(
    () => sortedTaggedFiles.filter(f => f.source === 'internal'),
    [sortedTaggedFiles]
  );

  const isLoading =
    sharedWithCustomerFilesStatus === 'pending' ||
    sharedByCustomerFilesStatus === 'pending' ||
    plextracReportsStatus === 'pending' ||
    exportFilesStatus === 'pending' ||
    (isPraetorianUser && internalFilesStatus === 'pending');

  const isAllEmpty = !isLoading && taggedFiles.length === 0;

  const noDataConfig = isAllEmpty
    ? createAllEmptyConfig({
        entityName: 'No Documents Yet',
        icon: <DocumentTextIcon className={`${NO_DATA_ICON_CLASS} size-12`} />,
        description: 'Upload your first file to get started',
        onGetStarted: canUpload ? () => setShowFileUpload(true) : undefined,
        actionLabel: 'Upload File',
      })
    : createFilteredEmptyConfig({
        entityName: 'documents',
        icon: <DocumentTextIcon className={`${NO_DATA_ICON_CLASS} size-12`} />,
        hasActiveFilters: !!search,
        onClearFilters: () => setSearch(''),
      });

  const onFilesDrop = (
    files: { content: string | ArrayBuffer; file: File }[],
    section: string
  ) => {
    const isInternal = section === 'internal';
    files.forEach(({ content, file }) => {
      setUploadProgress(0);
      uploadFile({
        name: `${query}/${section}/${file.name}`,
        content: content ?? '',
        ...(isInternal && { praetorian: true }),
        // The auto-delete picker only renders on the customer-facing zone;
        // internal uploads never expire.
        ...(uploadTtl && !isInternal && { ttl: uploadTtl }),
        onProgress: progressEvent => {
          const progress =
            (progressEvent.loaded / (progressEvent.total ?? 1)) * 100;
          setUploadProgress(progress >= 100 ? null : progress);
        },
      })
        .then(() => {
          setUploadProgress(null);
          setShowFileUpload(false);
          invalidateSharedWithCustomerFiles();
          invalidateSharedByCustomerFiles();
          invalidatePlextracReports();
          invalidateExportFiles();
          invalidateInternalFiles();
        })
        .catch(() => {
          setUploadProgress(null);
        });
    });
  };

  const handleDownload = (file: MyFile) => {
    const isInternal = internalFiles.some(f => f.name === file.name);
    downloadFile({ name: file.name, ...(isInternal && { praetorian: true }) });
  };

  const handleToggleFavorite = (file: MyFile) => {
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    } else {
      setFavorites([...favorites, file.name]);
    }
  };

  const handleDelete = async (file: MyFile) => {
    const isInternal = internalFiles.some(f => f.name === file.name);
    await deleteFile({
      name: file.name,
      ...(isInternal && { praetorian: true }),
    });
    // Remove from favorites if it was favorited
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    }
    // Invalidate all file queries
    invalidateSharedWithCustomerFiles();
    invalidateSharedByCustomerFiles();
    invalidatePlextracReports();
    invalidateExportFiles();
    invalidateInternalFiles();
  };

  const handleShare = (file: MyFile) => {
    const basePath = customerEmail
      ? `/${encodeURIComponent(encode(customerEmail))}`
      : `/${window.location.pathname.split('/').filter(Boolean)[0] ?? ''}`;
    const params = new URLSearchParams();
    params.append('file', file.name);
    const url = `${window.location.origin}${basePath}/files?${params.toString()}`;
    void navigator.clipboard.writeText(url);
    toast.success('Copied URL to clipboard');
  };

  const fileType = filename ? getFileType(filename) : undefined;
  const isViewingInternalFile = internalFiles.some(f => f.name === filename);
  const hasUnsavedChanges =
    content !== null && originalContent !== null && content !== originalContent;

  return (
    <div className="flex h-full flex-col gap-6 overflow-auto pr-4">
      {enableSearch && !isAllEmpty && (
        <>
          <div className="flex items-center justify-between">
            <div className="relative max-w-md grow">
              <input
                placeholder="Search documents..."
                value={search}
                name="file_search"
                onChange={e => setSearch(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-primary h-10 w-full rounded-md border px-4 pl-10"
              />
              <MagnifyingGlassIcon className="text-chariot-text-secondary absolute top-2.5 left-3 size-5" />
              {search.length > 0 && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute top-2.5 right-3"
                >
                  <XMarkIcon className="text-chariot-text-secondary size-5" />
                </button>
              )}
            </div>
          </div>

          <div className="bg-chariot-stroke h-px w-full"></div>
        </>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        {!isAllEmpty && (
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-bold">Documents</h2>
            {canUpload && (
              <Button onClick={() => setShowFileUpload(!showFileUpload)}>
                <DocumentPlusIcon className="size-5" />
                Upload File
              </Button>
            )}
          </div>
        )}

        {showFileUpload && (
          <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-6">
            <div className="mb-3 flex items-center justify-end gap-2">
              <span className="text-chariot-text-secondary text-xs font-semibold uppercase">
                Auto-delete
              </span>
              <FileTtlSelect
                value={uploadTtl}
                onChange={setUploadTtl}
                className="w-48"
              />
            </div>
            <DocumentUploadZone
              onFilesDrop={files => onFilesDrop(files, uploadSection)}
              uploadProgress={uploadProgress}
              subTitle={
                isPraetorianUser
                  ? 'Upload documents to share with the customer'
                  : 'Upload documents for analysis'
              }
            />
          </div>
        )}

        {isPraetorianUser && (
          <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Internal Documents</h3>
                <p className="text-chariot-text-secondary text-xs">
                  Visible to Praetorian team only
                </p>
              </div>
            </div>
            {canUpload && (
              <div className="mb-3">
                <DocumentUploadZone
                  onFilesDrop={files => onFilesDrop(files, 'internal')}
                  uploadProgress={uploadProgress}
                  subTitle="Upload internal documents (not visible to customers)"
                />
              </div>
            )}
            {sortedInternalFiles.length > 0 && (
              <div className="border-chariot-stroke flex flex-col rounded-md border">
                <FilesHeaderRow sort={sort} onChange={setSort} />
                {sortedInternalFiles.map(file => (
                  <DocumentListItem
                    key={file.name}
                    file={file}
                    onView={() => setFilename(file.name)}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onShare={handleShare}
                    isFavorite={
                      enableFavorites && favorites.includes(file.name)
                    }
                    onToggleFavorite={
                      enableFavorites ? handleToggleFavorite : undefined
                    }
                    displayName={file.displayName}
                    source="internal"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div
          className={cn(
            'border-chariot-stroke flex flex-1 flex-col rounded-md border',
            filteredFiles.length === 0 && !isLoading && 'p-4'
          )}
        >
          {!isAllEmpty && <FilesHeaderRow sort={sort} onChange={setSort} />}
          <Loader isLoading={isLoading}>
            <div className="flex flex-1 flex-col">
              {filteredFiles.length > 0 ? (
                filteredFiles.map(file => (
                  <DocumentListItem
                    key={file.name}
                    file={file}
                    onView={() => setFilename(file.name)}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onShare={handleShare}
                    isFavorite={
                      enableFavorites && favorites.includes(file.name)
                    }
                    onToggleFavorite={
                      enableFavorites ? handleToggleFavorite : undefined
                    }
                    displayName={file.displayName}
                    source={file.source}
                  />
                ))
              ) : (
                <NoData {...noDataConfig} />
              )}
            </div>
          </Loader>
        </div>
      </div>

      <Modal
        open={filename.length > 0}
        onClose={() => {
          setFilename('');
          setContent(null);
          setOriginalContent(null);
        }}
        size="6xl"
        title={
          <div className="flex w-full items-center justify-between">
            <span>{filename}</span>
            <div className="mr-8 flex items-center space-x-2">
              {fileType === ParsedFileTypes.DOCUMENT && (
                <Tooltip
                  title={
                    hasUnsavedChanges
                      ? 'Save Unsaved Changes'
                      : 'Save and Overwrite Existing'
                  }
                >
                  <div className="relative">
                    <Button
                      onClick={async () => {
                        uploadFile({
                          ignoreSnackbar: true,
                          name: filename,
                          content: content ?? '',
                          ...(isViewingInternalFile && { praetorian: true }),
                        }).then(() => {
                          setOriginalContent(content);
                        });
                      }}
                      variant="ghost"
                      className="hover:bg-chariot-background-secondary rounded-xs p-2"
                    >
                      <Save className="size-5" />
                    </Button>
                    {hasUnsavedChanges && (
                      <div className="border-chariot-background-primary absolute -top-1 -right-1 size-3 rounded-full border-2 bg-red-500"></div>
                    )}
                  </div>
                </Tooltip>
              )}
              <Tooltip title="Download">
                <Button
                  onClick={() => {
                    const file = files.find(f => f.name === filename);
                    if (file) handleDownload(file);
                  }}
                  variant="ghost"
                  className="hover:bg-chariot-background-secondary rounded-xs p-2"
                >
                  <ArrowDownOnSquareIcon className="size-5" />
                </Button>
              </Tooltip>
              {enableFavorites && (
                <Tooltip
                  title={
                    favorites.includes(filename)
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }
                >
                  <Button
                    onClick={() => {
                      if (favorites.includes(filename)) {
                        setFavorites(favorites.filter(fav => fav !== filename));
                      } else {
                        setFavorites([...favorites, filename]);
                      }
                    }}
                    variant="ghost"
                    className="hover:bg-chariot-background-secondary rounded-xs p-2"
                  >
                    <BookmarkIcon
                      className={`size-5 ${favorites.includes(filename) ? 'fill-current text-yellow-500' : ''}`}
                    />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        }
        hideFooter
      >
        <FileViewer
          key={filename}
          fileName={filename}
          email={customerEmail}
          praetorian={isViewingInternalFile}
          onChange={changed => {
            setContent(changed);
            if (originalContent === null) {
              setOriginalContent(changed);
            }
          }}
          onClose={() => {
            setFilename('');
            setContent(null);
            setOriginalContent(null);
          }}
        />
      </Modal>
    </div>
  );
};
